<template>
  <div
    :id="`comment-${comment.identifier}`"
    :class="commentClasses"
  >
    <avatar :user-data="comment.author" />
    <div class="message-format-right">
      <div class="message-format-header">
        <div class="message-format-name">
          {{ userFullName }}
        </div>
        <div
          v-if="isFromStaffUser"
          class="message-format-user-attribute"
        >
          {{ $t('global.moderator') }}
        </div>
        <div
          class="message-format-time"
        >
          {{ formattedCommentTime }}
          <span
            v-if="displayEditMenu"
            class="edit-comment"
            @click="editComment"
          >
            <b-tooltip
              :label="$t('global.edit')"
              position="is-top"
              type="is-dark"
            >
              <b-icon
                icon="pencil-alt"
                pack="fas"
              />
            </b-tooltip>
          </span>
        </div>
      </div>
      <media
        v-if="comment.mediaFile"
        class="comment-media"
        :media-file="comment.mediaFile"
        :media-width="700"
      />
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="message-format-content"
        v-html="comment.content"
      />
      <!-- eslint-enable vue/no-v-html -->
      <div
        v-if="displayParticipantTags && participantTags && participantTags.length > 0"
        class="message-format-footer"
      >
        <tag-list
          :tag-data="participantTags"
        />
      </div>
      <div
        v-if="comment.replyingToIdentifier === null"
        class="reply-button-container"
      >
        <b-button
          class="reply-button"
          size="is-small"
          type="is-info is-light"
          @click="replyTo(comment.identifier)"
        >
          {{ $t('comments.reply') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from 'timeago.js'
import { mapState } from 'vuex'

import Media from '@/components/Media.vue'
import Avatar from '../Avatar.vue'
import TagList from '../TagList.vue'

export default {
  name: 'Comment',
  components: {
    Avatar,
    Media,
    TagList
  },
  props: {
    comment: {
      type: Object,
      default: null
    },
    displayParticipantTags: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState('comments', ['updatedCommentIdentifier']),
    ...mapState('Projects', ['projectData']),
    ...mapState('Users', ['userData']),
    commentClasses() {
      const classes = ['comment-container', 'message-format-container', 'with-borders']

      if (this.updatedCommentIdentifier === this.comment.identifier) {
        classes.push('updated')
      }

      return classes
    },
    displayEditMenu() {
      return this.comment.author.identifier === this.userData.identifier
    },
    userFullName() {
      return `${this.comment.author.firstName} ${this.comment.author.lastName}`
    },
    formattedCommentTime() {
      return format(this.comment.created, this.$i18n.locale)
    },
    isFromStaffUser() {
      return this.projectData && this.projectData.staffUsers.some(
        (su) => su.userIdentifier === this.comment.author.identifier
      )
    },
    participantTags() {
      if (this.displayParticipantTags && !this.loading) {
        const participantData = this.$store.getters['staffParticipants/participantDataByUserIdentifier'](
          this.comment.author.identifier
        )

        if (participantData) return participantData.tags

        return null
      }

      return null
    }
  },
  methods: {
    editComment() {
      this.$store.commit('comments/startEditingComment', this.comment)
    },
    replyTo(commentIdentifier) {
      this.$store.commit('comments/replyTo', commentIdentifier)
    }
  }
}
</script>

<style lang="scss" scoped>
.comment-media {
  padding-top: 10px;
}

.edit-comment {
  display: inline-block;
  margin-left: 8px;
  cursor: pointer;

  &:hover {
    color: $gray-dark;
  }

  & ::v-deep .icon {
    height: auto;
    width: auto;
  }
}

.reply-button-container {
  margin-top: 6px;
  display: flex;
  justify-content: flex-end;
}
</style>
